<template>
  <div class="knowledge-detail-wrapper" v-if="current">
    <div class="knowledge-detail">
      <div class="kld-detail-header">
        <div class="kld-detail-header-main">
          <div class="kld-detail-icon">
            <BaseIcon :icon="getItemIcon(current)" :size="22" />
          </div>
          <div class="kld-detail-header-info">
            <div class="kld-detail-title-row">
              <h2 class="kld-detail-title" :title="displayName">{{ displayName }}</h2>
              <Tag v-if="current.ref" color="blue" class="kld-detail-tag">引用</Tag>
              <Tag class="kld-detail-tag kld-detail-tag--type">{{ typeLabel }}</Tag>
            </div>
            <div class="kld-detail-meta-grid">
              <div class="kld-meta-cell">
                <span class="kld-meta-label">上传人</span>
                <span class="kld-meta-value">
                  <BaseNameBoard
                    v-if="current.created_by || current.uploader"
                    :uid="current.created_by || current.uploader"
                    :size="16"
                  />
                  <span v-else class="kld-meta-empty">—</span>
                </span>
              </div>
              <div class="kld-meta-cell">
                <span class="kld-meta-label">上传时间</span>
                <span class="kld-meta-value">{{ formatDate(current.created_at) || '—' }}</span>
              </div>
              <div class="kld-meta-cell" v-if="current.updated_by || current.modifier || current.updated_at">
                <span class="kld-meta-label">修改人</span>
                <span class="kld-meta-value">
                  <BaseNameBoard
                    v-if="current.updated_by || current.modifier"
                    :uid="current.updated_by || current.modifier"
                    :size="16"
                  />
                  <span v-else class="kld-meta-empty">—</span>
                </span>
              </div>
              <div class="kld-meta-cell" v-if="current.updated_at">
                <span class="kld-meta-label">修改时间</span>
                <span class="kld-meta-value">{{ formatDate(current.updated_at) }}</span>
              </div>
            </div>
          </div>
          <a
            v-if="isFileType && fileUrl"
            :href="fileUrl"
            target="_blank"
            class="kld-detail-download"
          >
            <BaseIcon icon="md-download" :size="16" />
            <span>下载</span>
          </a>
        </div>
      </div>

      <div
        class="detail-content"
        :class="{ 'detail-content--fill': isFillPreview }"
      >
        <div v-if="current.ref" class="ref-notice">
          <BaseIcon icon="md-information-circle" :size="16" class="ref-notice-icon" />
          <span class="ref-notice-text">引用文件，展示源文件内容</span>
          <Button type="text" size="small" @click="handleOpenSource" class="ref-notice-btn">查看源文件</Button>
        </div>

        <div v-if="sourceType === 'file'" class="file-viewer">
          <div v-if="fileUrl" class="kng-file-preview">
            <BasePreview
              v-if="isPreviewableFile"
              :url="fileUrl"
              :name="displayName"
              class="preview-fill"
            />
            <div v-else class="preview-empty">
              <BaseEmpty type="content" msg="该文件格式不支持在线预览，请下载后查看" />
            </div>
          </div>
          <div v-else class="preview-empty">
            <BaseEmpty type="empty-content" msg="此档案未上传附件" />
          </div>
        </div>

        <div v-else-if="isMarkdownView" class="markdown-viewer">
          <BaseMarkdownView :value="markdownSource" fill plain />
        </div>

        <div v-else-if="sourceType === 'text'" class="text-viewer">
          <pre class="text-content">{{ fileUrl || '暂无内容' }}</pre>
        </div>

        <div v-else-if="sourceType === 'html'" class="html-viewer">
          <div class="html-content" v-html="fileUrl"></div>
        </div>

        <div v-else class="unknown-viewer">
          <div class="unknown-content">{{ fileUrl || '未知类型' }}</div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="knowledge-detail-wrapper">
    <div class="empty-detail">
      <BaseEmpty type="empty" msg="请选择一个项目查看详情" />
    </div>
  </div>
</template>

<script>
import BaseEmpty from '@/components/base/Empty';
import BaseIcon from '@/components/base/Icon';
import BaseNameBoard from '@/components/base/NameBoard';
import BasePreview from '@/components/base/Preview';
import { looksLikeMarkdownContent } from '@/pages/core/documents/document-helpers';
import moment from 'moment';

const TYPE_LABELS = {
  file: '文件',
  text: '文本',
  md: 'Markdown',
  html: 'HTML',
};

export default {
  components: {BaseEmpty, BaseIcon, BaseNameBoard, BasePreview},
  props: {
    current: {
      type: Object,
      default: null,
    },
    allItems: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    sourceItem() {
      return this.current ? this.getSourceItem(this.current) : null;
    },
    sourceType() {
      return this.sourceItem ? this.sourceItem.type : '';
    },
    fileUrl() {
      return this.current ? this.getSourceContent(this.current) : '';
    },
    displayName() {
      return this.current ? this.getItemName(this.current) : '';
    },
    typeLabel() {
      return TYPE_LABELS[this.sourceType] || '内容';
    },
    isFileType() {
      return this.sourceType === 'file';
    },
    isPreviewableFile() {
      if (!this.fileUrl) return false;
      const ext = this.getFileExt(this.fileUrl);
      const previewableExts = [
        'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico',
        'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf',
      ];
      return previewableExts.includes(ext);
    },
    isMarkdownView() {
      if (!this.current) return false;
      if (this.sourceType === 'md') return true;
      if (this.sourceType === 'text') return looksLikeMarkdownContent(this.fileUrl);
      return false;
    },
    markdownSource() {
      return this.fileUrl || '';
    },
    isFillPreview() {
      if (this.isMarkdownView) return true;
      return this.isFileType && !!this.fileUrl && this.isPreviewableFile;
    },
  },
  methods: {
    getItemIcon(item) {
      if (item.ref && item.content) {
        const sourceItem = this.allItems.find(i => i.id === item.content);
        if (sourceItem) {
          return this.getItemIcon(sourceItem);
        }
      }

      if (item.type === 'file' && item.content) {
        const url = item.content;
        const fileName = url.split('/').pop() || '';
        const ext = fileName.split('.').pop().toLowerCase();
        return this.getFileIconByExt(ext);
      }

      const iconMap = {
        text: 'md-document-text',
        md: 'codelibrary-fill',
        html: 'css',
      };
      return iconMap[item.type] || 'wenjian';
    },
    getFileIconByExt(ext) {
      const extIconMap = {
        txt: 'md-document-text',
        doc: 'md-document',
        docx: 'md-document',
        pdf: 'md-document',
        xls: 'md-document',
        xlsx: 'md-document',
        ppt: 'md-document',
        pptx: 'md-document',
        js: 'codelibrary-fill',
        ts: 'codelibrary-fill',
        jsx: 'codelibrary-fill',
        tsx: 'codelibrary-fill',
        vue: 'codelibrary-fill',
        py: 'codelibrary-fill',
        java: 'codelibrary-fill',
        cpp: 'codelibrary-fill',
        c: 'codelibrary-fill',
        go: 'codelibrary-fill',
        php: 'codelibrary-fill',
        rb: 'codelibrary-fill',
        md: 'codelibrary-fill',
        html: 'css',
        htm: 'css',
        xml: 'css',
        json: 'codelibrary-fill',
        yaml: 'codelibrary-fill',
        yml: 'codelibrary-fill',
        jpg: 'images',
        jpeg: 'images',
        png: 'images',
        gif: 'images',
        svg: 'images',
        webp: 'images',
        bmp: 'images',
        zip: 'wenjian',
        rar: 'wenjian',
        '7z': 'wenjian',
        tar: 'wenjian',
        gz: 'wenjian',
        mp4: 'shipin',
        avi: 'shipin',
        mov: 'shipin',
        mp3: 'shipin',
        wav: 'shipin',
      };
      return extIconMap[ext] || 'wenjian';
    },
    getItemName(item) {
      if (item.ref && item.content) {
        const sourceItem = this.allItems.find(i => i.id === item.content);
        if (sourceItem) {
          return this.getItemName(sourceItem);
        }
      }

      if (item.type === 'file' && item.content) {
        const url = item.content;
        let fileName = url.split('/').pop() || '未命名文件';
        const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
        if (item.name && ext) {
          fileName = `${item.name}.${ext}`;
        } else {
          return fileName;
        }
        return fileName;
      }
      if (item.type === 'text' || item.type === 'md' || item.type === 'html') {
        const content = item.content || '';
        const firstLine = content.split('\n')[0] || '未命名文档';
        return firstLine.substring(0, 50) + (firstLine.length > 50 ? '...' : '');
      }
      return item.name || '未命名';
    },
    formatDate(date) {
      if (!date) return '';
      return moment(date).format('YYYY-MM-DD HH:mm');
    },
    getFileExt(url) {
      if (!url) return '';
      const fileName = url.split('/').pop() || '';
      return fileName.split('.').pop().toLowerCase();
    },
    getSourceItem(item) {
      if (item.ref && item.content) {
        const sourceItem = this.allItems.find(i => i.id === item.content);
        return sourceItem || item;
      }
      return item;
    },
    getSourceContent(item) {
      if (item.ref && item.content) {
        const sourceItem = this.allItems.find(i => i.id === item.content);
        return sourceItem ? sourceItem.content : item.content;
      }
      return item.content;
    },
    handleOpenSource() {
      if (this.current.ref && this.current.content) {
        this.$emit('on-open-source', this.current.content);
      }
    },
  },
};
</script>

<style lang="less" scoped>
.knowledge-detail-wrapper {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;

  .knowledge-detail {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .kld-detail-header {
    flex-shrink: 0;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
    background: linear-gradient(180deg, var(--bg3) 0%, #fff 100%);
  }

  .kld-detail-header-main {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
  }

  .kld-detail-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--primary-light);
    color: var(--primary);
    flex-shrink: 0;
    border: 1px solid rgba(51, 153, 255, 0.15);
  }

  .kld-detail-header-info {
    flex: 1;
    min-width: 0;
  }

  .kld-detail-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    margin-bottom: 8px;
  }

  .kld-detail-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .kld-detail-tag {
    flex-shrink: 0;
    margin: 0;
    font-size: 11px;
    line-height: 18px;
    height: 20px;
    padding: 0 6px;
    border-radius: 4px;
  }

  .kld-detail-tag--type {
    color: var(--subtext2);
    background: var(--bg3);
    border: 1px solid var(--border);
  }

  .kld-detail-meta-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 20px;
  }

  .kld-meta-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    font-size: 12px;
    line-height: 20px;
  }

  .kld-meta-label {
    flex-shrink: 0;
    color: var(--subtext3);
    min-width: 52px;
  }

  .kld-meta-value {
    color: var(--text);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .kld-meta-empty {
    color: var(--subtext3);
  }

  .kld-detail-download {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    border: 1px solid var(--border);
    border-radius: 6px;
    background: #fff;
    color: var(--subtext2);
    font-size: 12px;
    text-decoration: none;
    transition: all 0.2s;

    &:hover {
      color: var(--primary);
      border-color: var(--primary);
      background: var(--primary-light);
    }
  }

  .detail-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
    background: #fff;
    box-sizing: border-box;
  }

  .detail-content--fill {
    padding: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .ref-notice {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 8px 16px;
    background: #f0f7ff;
    border-bottom: 1px solid #d6e8ff;
    font-size: 12px;

    .ref-notice-icon {
      color: #1890ff;
      flex-shrink: 0;
    }

    .ref-notice-text {
      flex: 1;
      color: #3a6ea5;
    }

    .ref-notice-btn {
      color: #1890ff;
      padding: 0;
    }
  }

  .file-viewer {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .kng-file-preview {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .preview-fill {
    flex: 1;
    min-height: 0;
    height: 100%;
    width: 100%;

    /deep/ .base-preview-root {
      flex: 1;
      min-height: 0;
      height: 100%;
      width: 100%;
      overflow: hidden;
    }

    /deep/ .base-preview {
      min-height: 0;
      min-width: 0;
      max-width: 100%;
    }

    /deep/ .base-preview__image {
      max-height: 100%;
    }
  }

  .preview-empty {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg2);

    /deep/ .l-empty-holder {
      width: 100%;
      height: 100%;
    }
  }

  .markdown-viewer {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 16px;
    background: var(--bg2);
    box-sizing: border-box;
  }

  .text-viewer {
    .text-content {
      background: var(--bg3);
      padding: 20px;
      border-radius: 6px;
      font-family: 'Courier New', 'Consolas', 'Monaco', monospace;
      font-size: 14px;
      line-height: 1.8;
      white-space: pre-wrap;
      word-wrap: break-word;
      margin: 0;
      border: 1px solid var(--border);
      color: var(--text);
    }
  }

  .html-viewer {
    .html-content {
      padding: 20px;
      background: #fff;
      border-radius: 6px;
      border: 1px solid var(--border);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }
  }

  .unknown-viewer {
    .unknown-content {
      padding: 40px 20px;
      text-align: center;
      color: var(--subtext2);
      font-size: 14px;
    }
  }

  .empty-detail {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
  }
}
</style>
